import clsx from "clsx";
import * as React from "react";
import { Button } from "../button/Button.js";
import { Caption } from "../caption/Caption.js";
import { Heading } from "../heading/Heading.js";
import { Text } from "../text/Text.js";

interface BaseStatisticProps extends React.HTMLAttributes<HTMLDivElement> {
  withAction?: {
    label?: string;
    onClick?: () => void;
    startIcon?: React.ReactElement;
    endIcon?: React.ReactElement;
  };
  title: string;
  value: string | number;
  valueIcon?: { type?: "positive" | "negative"; icon?: React.ReactNode };
  trendTitle?: {
    title?: string;
    valueIcon?: string;
    value?: string | number;
    trend?: "positive" | "negative";
  };
  color?:
    | "blue"
    | "yellow"
    | "purple"
    | "gray"
    | "red"
    | "indigo"
    | "lightblue";
  selected?: boolean;
  className?: string;
  classNames?: {
    root?: string;
    headerIcon?: string;
    content?: string;
    title?: string;
    value?: string;
    valueIcon?: string;
    trend?: string;
    trendTitle?: string;
    trendValue?: string;
    trendValueIcon?: string;
    action?: string;
  };
}

interface StatisticIconProps extends BaseStatisticProps {
  icon?: React.ReactNode;
  position?: "start" | "end";
}
interface NonStatisticIconProps extends BaseStatisticProps {
  icon?: undefined;
  position?: never;
}

type StatisticProps = StatisticIconProps | NonStatisticIconProps;

const Statistic = React.forwardRef<HTMLDivElement, StatisticProps>(
  (
    {
      withAction,
      selected = false,
      color = "blue",
      position = "start",
      title,
      value,
      icon,
      trendTitle,
      valueIcon,
      className,
      classNames,
      ...rest
    },
    ref,
  ) => {
    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (withAction?.onClick) {
          event.stopPropagation();
          withAction?.onClick();
        }
      },
      [withAction?.onClick],
    );

    return (
      <div
        ref={ref}
        data-selected={selected}
        data-color={color}
        data-position={position}
        className={clsx(
          "ox-reset",
          "ox-Statistic",
          className,
          classNames?.root,
        )}
        {...rest}
      >
        {icon && (
          <div
            className={clsx(
              "ox-reset ox-StatisticHeaderIcon",
              classNames?.headerIcon,
            )}
            data-color={color}
            data-selected={selected}
          >
            {icon}
          </div>
        )}
        <div
          className={clsx(
            "ox-reset",
            "ox-StatisticContent",
            classNames?.content,
          )}
        >
          <Text
            size="sm"
            fontWeight="medium"
            className={clsx("ox-reset", "ox-StatisticTitle", classNames?.title)}
          >
            {title}
          </Text>
          <div
            className={clsx("ox-reset", "ox-StatisticValue", classNames?.value)}
          >
            {valueIcon?.icon ? (
              <div
                className={clsx(
                  "ox-reset",
                  "ox-StatisticValueIcon",
                  classNames?.valueIcon,
                )}
                data-type={valueIcon?.type}
              >
                {valueIcon?.icon}
              </div>
            ) : null}
            <Heading as="h3" fontWeight="semibold">
              {value}
            </Heading>
          </div>
          {trendTitle ? (
            <div
              className={clsx(
                "ox-reset",
                "ox-StatisticTrend",
                classNames?.trend,
              )}
            >
              <Caption
                size="lg"
                className={clsx(
                  "ox-reset",
                  "ox-TrendTitle",
                  classNames?.trendTitle,
                )}
              >
                {trendTitle.title}
              </Caption>
              <Caption
                size="lg"
                data-trend={trendTitle.trend}
                fontWeight="medium"
                className={clsx(
                  "ox-reset",
                  "ox-TrendValue",
                  classNames?.trendValue,
                )}
              >
                <span
                  className={clsx(
                    "ox-reset",
                    "ox-TrendValueIcon",
                    classNames?.trendValueIcon,
                  )}
                >
                  {trendTitle.trend === "positive" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M213.66,165.66a8,8,0,0,1-11.32,0L128,91.31,53.66,165.66a8,8,0,0,1-11.32-11.32l80-80a8,8,0,0,1,11.32,0l80,80A8,8,0,0,1,213.66,165.66Z"></path>
                    </svg>
                  ) : trendTitle.trend === "negative" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                    </svg>
                  ) : null}
                </span>
                {trendTitle.value}
              </Caption>
            </div>
          ) : null}
          {withAction ? (
            <div>
              <Button
                variant="link"
                size="sm"
                startIcon={withAction.startIcon || undefined}
                endIcon={withAction.endIcon || undefined}
                onClick={handleClick}
                className={clsx(
                  "ox-reset",
                  "ox-StatisticAction",
                  classNames?.action,
                )}
              >
                {withAction.label || "Action"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

Statistic.displayName = "Statistic";
export { Statistic };
export type { StatisticProps };
